import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { type ReactNode, useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import { Button } from '../../components/ui/Button.js';
import { Card } from '../../components/ui/Card.js';
import { FormField, Input, Select, Textarea } from '../../components/ui/FormField.js';
import { Modal } from '../../components/ui/Modal.js';
import { PageHeader } from '../../components/ui/PageHeader.js';
import { Spinner } from '../../components/ui/Spinner.js';
import { useAbility } from '../../context/AbilityContext.js';
import { api } from '../../services/api.js';
import { PenaltyDialog } from './PenaltyDialog.js';
import { PenaltyReceiptDialog } from './PenaltyReceiptDialog.js';
import {
  PENALTY_TYPES,
  PENALTY_TYPE_LABEL,
  type Penalty,
  type PenaltyList,
  dayWords,
  mytToday,
  penaltiesToCsv,
  penaltyPowers,
  ratePercent,
  rm,
  totalsLine,
  uploadedWords,
} from './penalties.js';

/**
 * 371 (#1241): every customer penalty, in place of the CRM sheet's penalty tab. Staff with `manage Penalty` record,
 * edit, cancel, and mark each one paid by the customer and paid out to the supplier (377: money is the supplier's). Active penalties count in the Sales
 * Statement's Penalty row; a cancelled one stays listed, struck through, and counts nowhere. Figures are the server's.
 */
const TH = 'px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-steel';
const TD = 'px-3 py-2 text-sm text-ink align-top';

function download(name: string, text: string) {
  const url = URL.createObjectURL(new Blob([text], { type: 'text/csv;charset=utf-8' }));
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  a.click();
  URL.revokeObjectURL(url);
}

type Ask =
  | { kind: 'cancel'; penalty: Penalty }
  | { kind: 'paid' | 'payout'; penalty: Penalty }
  | null;

/** Cancel (with a reason) or mark paid / paid out (on a day). */
function AskDialog({ ask, onClose }: { ask: NonNullable<Ask>; onClose: () => void }) {
  const qc = useQueryClient();
  const [text, setText] = useState(ask.kind === 'cancel' ? '' : mytToday());
  const [problem, setProblem] = useState<string | null>(null);
  const run = useMutation({
    mutationFn: () =>
      api.post(
        `/penalties/${ask.penalty.id}/${ask.kind}`,
        ask.kind === 'cancel' ? { reason: text.trim() } : { on: text },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['penalties'] });
      onClose();
    },
    onError: (e) => setProblem((e as { message?: string }).message ?? 'Save failed.'),
  });
  const title =
    ask.kind === 'cancel'
      ? 'Cancel penalty'
      : ask.kind === 'paid'
        ? 'Paid by the customer'
        : 'Paid out to the supplier';
  return (
    <Modal
      open
      onClose={onClose}
      title={title}
      size="sm"
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>
            Back
          </Button>
          <Button
            variant={ask.kind === 'cancel' ? 'danger' : 'primary'}
            disabled={run.isPending || !text.trim()}
            onClick={() => run.mutate()}
          >
            {ask.kind === 'cancel' ? 'Cancel penalty' : 'Save'}
          </Button>
        </div>
      }
    >
      <p className="mb-3 text-sm text-ink">
        {`${ask.penalty.booking_ref} · ${ask.penalty.customer.name} · ${rm(ask.penalty.amount_minor)}`}
      </p>
      {ask.kind === 'payout' && (
        // 409 (#1471, mock-up frame 3): the amount to transfer, so nobody works it out by hand.
        <dl
          data-testid="payout-split"
          className="mb-3 grid grid-cols-[1fr_auto] gap-x-4 gap-y-1 text-sm"
        >
          <dt className="text-steel">Fine</dt>
          <dd className="text-right">{rm(ask.penalty.amount_minor)}</dd>
          <dt className="text-steel">
            CARii commission ({ratePercent(ask.penalty.commission.rate_bps)})
          </dt>
          <dd className="text-right">− {rm(ask.penalty.commission.commission_minor)}</dd>
          <dt className="font-semibold text-ink">Pay out</dt>
          <dd className="text-right text-base font-bold text-semantic-success-text">
            {rm(ask.penalty.commission.payout_minor)}
          </dd>
        </dl>
      )}
      {ask.kind === 'cancel' ? (
        <FormField label="Why is it cancelled?" name="penalty-cancel-reason">
          <Textarea
            id="penalty-cancel-reason"
            rows={2}
            maxLength={300}
            value={text}
            onChange={(e) => setText(e.target.value)}
          />
        </FormField>
      ) : (
        <FormField label="On" name="penalty-mark-on">
          <Input
            id="penalty-mark-on"
            type="date"
            max={mytToday()}
            value={text}
            onChange={(e) => setText(e.target.value)}
          />
        </FormField>
      )}
      <p className="mt-2 text-xs text-steel">
        {ask.kind === 'cancel'
          ? 'A cancelled penalty stays listed but no longer counts in the Sales Statement.'
          : 'This changes no figure in the Sales Statement.'}
      </p>
      {problem && (
        <p role="alert" className="mt-2 text-sm text-semantic-danger-text">
          {problem}
        </p>
      )}
    </Modal>
  );
}

/** A paid / paid-out cell: the day with Undo, or the mark button. */
function MarkCell({
  p,
  day,
  done,
  none,
  markLabel,
  onMark,
  onUndo,
  mayAct,
}: {
  p: Penalty;
  day: string | null;
  /** 409: what a done mark says before its day (the payout's amount). */
  done?: string;
  none: string;
  markLabel: string;
  onMark: () => void;
  onUndo: () => void;
  /** 412: this member holds the power the mark needs (confirm-payment or payout); otherwise the state only. */
  mayAct: boolean;
}): ReactNode {
  if (day)
    return (
      <>
        <div className="text-semantic-success-text">
          {done ? `${done} · ${dayWords(day)}` : dayWords(day)}
        </div>
        {p.status === 'active' && mayAct && (
          <button type="button" className="text-xs text-steel underline" onClick={onUndo}>
            Undo
          </button>
        )}
      </>
    );
  return (
    <>
      <div className="text-semantic-warning-text">{none}</div>
      {p.status === 'active' && mayAct && (
        <button type="button" className="text-xs text-primary underline" onClick={onMark}>
          {markLabel}
        </button>
      )}
    </>
  );
}

/** 393: the customer's receipt under the paid cell: waiting (with "Check receipt") or rejected (with why). */
function ReceiptLine({
  p,
  onCheck,
  mayDecide,
}: { p: Penalty; onCheck: () => void; mayDecide: boolean }) {
  if (p.customer_paid_on || p.status === 'cancelled') return null;
  if (p.receipt_status === 'under_review') {
    return (
      <div className="mt-1">
        <span className="inline-block rounded bg-yellow-100 px-2 py-0.5 text-xs font-semibold text-yellow-800 dark:bg-yellow-500/15 dark:text-yellow-200">
          Receipt under review
        </span>
        {p.receipt_uploaded_at && (
          <div data-testid="receipt-uploaded" className="text-xs text-steel">
            uploaded {uploadedWords(p.receipt_uploaded_at)}
          </div>
        )}
        {/* 412: without confirm-payment the receipt opens read-only. */}
        <button type="button" className="text-xs text-primary underline" onClick={onCheck}>
          {mayDecide ? 'Check receipt' : 'View receipt'}
        </button>
      </div>
    );
  }
  if (p.receipt_status === 'rejected') {
    return (
      <div className="mt-1 text-xs text-semantic-danger-text">
        Receipt rejected{p.receipt_reject_reason ? `: ${p.receipt_reject_reason}` : ''}
      </div>
    );
  }
  return null;
}

export function PenaltyListPage() {
  const qc = useQueryClient();
  // 412 (#1505, mock-up frames 1–3): each control follows the power the server asks for.
  const can = penaltyPowers(useAbility());
  const [search] = useSearchParams();
  // 397 (#1328): the dashboard's click-through sets the filters in the address; an unknown value is ignored.
  const fromAddress = (key: string, allowed?: readonly string[]) => {
    const v = search.get(key) ?? '';
    return allowed && !allowed.includes(v) ? '' : v;
  };
  const DAY = /^\d{4}-\d{2}-\d{2}$/;
  const [q, setQ] = useState(search.get('q') ?? '');
  const [supplier, setSupplier] = useState(() => fromAddress('supplier_id'));
  const [type, setType] = useState('');
  const [from, setFrom] = useState(() =>
    DAY.test(fromAddress('from')) ? fromAddress('from') : `${mytToday().slice(0, 7)}-01`,
  );
  const [to, setTo] = useState(() =>
    DAY.test(fromAddress('to')) ? fromAddress('to') : mytToday(),
  );
  const [paid, setPaid] = useState(() => fromAddress('paid', ['paid', 'unpaid']));
  const [payout, setPayout] = useState(() => fromAddress('payout', ['pending', 'done']));
  const [status, setStatus] = useState(() => fromAddress('status', ['active', 'cancelled']));
  const [car, setCar] = useState(() => fromAddress('car_id'));
  // 393: receipts waiting for a decision, whatever the dates.
  const [receipts, setReceipts] = useState(false);
  const [checking, setChecking] = useState<Penalty | null>(null);
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState<Penalty | null>(null);
  const [ask, setAsk] = useState<Ask>(null);

  const params = new URLSearchParams();
  if (q.trim()) params.set('q', q.trim());
  if (supplier) params.set('supplier_id', supplier);
  if (type) params.set('type', type);
  if (receipts) params.set('receipt', 'under_review');
  if (from && !receipts) params.set('from', from);
  if (to && !receipts) params.set('to', to);
  if (paid) params.set('paid', paid);
  if (payout) params.set('payout', payout);
  if (status) params.set('status', status);
  if (car) params.set('car_id', car);
  const url = `/penalties?${params.toString()}`;
  const list = useQuery({
    queryKey: ['penalties', url],
    queryFn: () => api.get(url) as Promise<PenaltyList>,
  });
  // The supplier choices are the suppliers of the penalties listed (a CRM role need not read suppliers).
  const [suppliers, setSuppliers] = useState<Record<string, string>>({});
  useEffect(() => {
    const items = list.data?.items ?? [];
    if (items.length === 0) return;
    setSuppliers((s) => ({
      ...s,
      ...Object.fromEntries(items.map((p) => [p.supplier.id, p.supplier.name])),
    }));
  }, [list.data]);

  const undo = useMutation({
    mutationFn: ({ p, kind }: { p: Penalty; kind: 'paid' | 'payout' }) =>
      api.post(`/penalties/${p.id}/${kind}`, { on: null }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['penalties'] }),
  });

  const data = list.data;
  const carPlate = car ? data?.items.find((p) => p.car.id === car)?.car.plate_number : undefined;
  return (
    <div className="flex flex-col gap-4">
      <PageHeader
        title="Penalties"
        description="Penalties charged to customers. Active ones count in the Sales Statement on their day."
        actions={
          can.create ? <Button onClick={() => setAdding(true)}>+ Add penalty</Button> : undefined
        }
      />
      {/* 393 (#1313, mock-up screen 5): receipts customers uploaded, waiting for a decision. */}
      <div className="flex flex-wrap gap-2">
        <Button
          variant={receipts ? 'primary' : 'secondary'}
          aria-pressed={receipts}
          onClick={() => setReceipts((v) => !v)}
        >
          {`Receipt under review (${data?.totals.under_review_count ?? 0})`}
        </Button>
        {/* 397: one car's penalties (the dashboard's click-through); there is no car control, so it is a chip. */}
        {car && (
          <button
            type="button"
            aria-label={`Remove car filter ${carPlate ?? 'the chosen car'}`}
            onClick={() => setCar('')}
            className="inline-flex items-center gap-1.5 rounded-full border border-brand-blue bg-brand-blue-soft px-3 py-1 text-sm font-medium text-brand-blue-dark"
          >
            {`Car: ${carPlate ?? 'the chosen car'}`}
            <span aria-hidden="true">×</span>
          </button>
        )}
      </div>
      <Card>
        <div className="grid gap-3 sm:grid-cols-3 lg:grid-cols-7">
          <div className="sm:col-span-3 lg:col-span-7">
            <FormField label="Search" name="pen-q">
              <Input
                id="pen-q"
                value={q}
                placeholder="Booking, customer, phone or plate"
                onChange={(e) => setQ(e.target.value)}
              />
            </FormField>
          </div>
          <FormField label="Supplier" name="pen-supplier">
            <Select
              id="pen-supplier"
              value={supplier}
              onChange={(e) => setSupplier(e.target.value)}
            >
              <option value="">All suppliers</option>
              {Object.entries(suppliers)
                .sort((a, b) => a[1].localeCompare(b[1]))
                .map(([id, name]) => (
                  <option key={id} value={id}>
                    {name || id.slice(-8)}
                  </option>
                ))}
            </Select>
          </FormField>
          <FormField label="Type" name="pen-type">
            <Select id="pen-type" value={type} onChange={(e) => setType(e.target.value)}>
              <option value="">All types</option>
              {PENALTY_TYPES.map((t) => (
                <option key={t} value={t}>
                  {PENALTY_TYPE_LABEL[t]}
                </option>
              ))}
            </Select>
          </FormField>
          <FormField label="From" name="pen-from">
            <Input
              id="pen-from"
              type="date"
              value={from}
              onChange={(e) => setFrom(e.target.value)}
            />
          </FormField>
          <FormField label="To" name="pen-to">
            <Input id="pen-to" type="date" value={to} onChange={(e) => setTo(e.target.value)} />
          </FormField>
          <FormField label="Customer paid" name="pen-paid">
            <Select id="pen-paid" value={paid} onChange={(e) => setPaid(e.target.value)}>
              <option value="">All</option>
              <option value="unpaid">Unpaid</option>
              <option value="paid">Paid</option>
            </Select>
          </FormField>
          <FormField label="Status" name="pen-status">
            <Select id="pen-status" value={status} onChange={(e) => setStatus(e.target.value)}>
              <option value="">All</option>
              <option value="active">Active</option>
              <option value="cancelled">Cancelled</option>
            </Select>
          </FormField>
          <FormField label="Supplier payout" name="pen-payout">
            <Select id="pen-payout" value={payout} onChange={(e) => setPayout(e.target.value)}>
              <option value="">All</option>
              <option value="pending">Pending</option>
              <option value="done">Done</option>
            </Select>
          </FormField>
        </div>
      </Card>

      {list.isLoading && <Spinner />}
      {list.error && (
        <p className="text-sm text-semantic-danger-text">
          {(list.error as { message?: string }).message ?? 'The penalties could not be loaded.'}
        </p>
      )}
      {data?.truncated && (
        <p className="text-sm text-semantic-warning-text">
          More than 5,000 match: only the newest 5,000 are shown. Narrow the dates to see the rest.
        </p>
      )}
      {data && (
        <Card>
          <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
            <p data-testid="penalty-totals" className="text-sm font-medium text-ink">
              {totalsLine(data.totals)}
            </p>
            <Button
              variant="secondary"
              onClick={() => download(`penalties-${from}-${to}.csv`, penaltiesToCsv(data))}
            >
              Export CSV
            </Button>
          </div>
          {data.items.length === 0 ? (
            <p className="text-sm text-steel">No penalty matches.</p>
          ) : (
            <div className="overflow-x-auto">
              <table aria-label="Penalties" className="min-w-full">
                <thead>
                  <tr>
                    {[
                      'Day',
                      'Customer',
                      'Booking · car',
                      'Type · reason',
                      'Amount',
                      'CARii',
                      'Payout',
                      'Customer paid',
                      'Supplier payout',
                      'By',
                      '',
                    ].map((h) => (
                      <th key={h || 'actions'} className={TH}>
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {data.items.map((p) => {
                    const off = p.status === 'cancelled';
                    const struck = off ? 'line-through text-steel' : '';
                    return (
                      <tr
                        key={p.id}
                        data-status={p.status}
                        className="border-t border-hairline-soft"
                      >
                        <td className={`${TD} whitespace-nowrap`}>{dayWords(p.on)}</td>
                        <td className={TD}>
                          <div>{p.customer.name || '—'}</div>
                          <div className="text-xs text-steel">{p.customer.phone}</div>
                        </td>
                        <td className={TD}>
                          <Link
                            className="text-brand-blue underline"
                            to={`/bookings/${p.booking_id}`}
                          >
                            {p.booking_ref}
                          </Link>
                          {p.rental_ref !== p.booking_ref && (
                            <span className="text-xs text-steel"> (rental {p.rental_ref})</span>
                          )}
                          <div className="text-xs text-steel">
                            {[p.car.plate_number, p.supplier.name].filter(Boolean).join(' · ')}
                          </div>
                        </td>
                        <td className={TD}>
                          <div className="font-medium">{PENALTY_TYPE_LABEL[p.type] ?? p.type}</div>
                          <div className="text-xs text-steel">{p.reason}</div>
                          {off && (
                            <div className="text-xs text-semantic-danger-text">
                              Cancelled{p.cancel_reason ? `: ${p.cancel_reason}` : ''}
                            </div>
                          )}
                        </td>
                        <td className={`${TD} whitespace-nowrap font-medium ${struck}`}>
                          {rm(p.amount_minor)}
                        </td>
                        {/* 409 (#1471, mock-up frame 2): CARii's share at its rate, and the supplier's payout. */}
                        <td
                          data-testid="penalty-commission"
                          className={`${TD} whitespace-nowrap ${struck}`}
                        >
                          <div>{rm(p.commission.commission_minor)}</div>
                          <div className="text-xs text-steel">
                            {ratePercent(p.commission.rate_bps)}
                          </div>
                        </td>
                        <td
                          data-testid="penalty-payout"
                          className={`${TD} whitespace-nowrap font-semibold ${struck}`}
                        >
                          {rm(p.commission.payout_minor)}
                        </td>
                        <td className={TD}>
                          <MarkCell
                            p={p}
                            day={p.customer_paid_on}
                            none="Unpaid"
                            markLabel="Mark paid"
                            onMark={() => setAsk({ kind: 'paid', penalty: p })}
                            onUndo={() => undo.mutate({ p, kind: 'paid' })}
                            mayAct={can.confirm}
                          />
                          <ReceiptLine
                            p={p}
                            onCheck={() => setChecking(p)}
                            mayDecide={can.confirm}
                          />
                        </td>
                        <td className={TD}>
                          <MarkCell
                            p={p}
                            day={p.host_paid_out_on}
                            done={`Paid out ${rm(p.commission.payout_minor)}`}
                            none="Pending"
                            markLabel="Mark paid out"
                            onMark={() => setAsk({ kind: 'payout', penalty: p })}
                            onUndo={() => undo.mutate({ p, kind: 'payout' })}
                            mayAct={can.payout}
                          />
                        </td>
                        <td className={TD}>{p.created_by_name || '—'}</td>
                        <td className={`${TD} whitespace-nowrap`}>
                          {!off && can.update && (
                            <>
                              <button
                                type="button"
                                className="text-xs text-primary underline"
                                onClick={() => setEditing(p)}
                              >
                                Edit
                              </button>{' '}
                              <button
                                type="button"
                                className="text-xs text-semantic-danger-text underline"
                                onClick={() => setAsk({ kind: 'cancel', penalty: p })}
                              >
                                Cancel
                              </button>
                            </>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      )}

      {adding && <PenaltyDialog open onClose={() => setAdding(false)} />}
      {editing && <PenaltyDialog open penalty={editing} onClose={() => setEditing(null)} />}
      {ask && <AskDialog ask={ask} onClose={() => setAsk(null)} />}
      {checking && (
        <PenaltyReceiptDialog
          penalty={checking}
          mayDecide={can.confirm}
          onClose={() => setChecking(null)}
        />
      )}
    </div>
  );
}
